import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../ui/Card';
import { getPredictionTier } from '../../utils/scores';
import { ScoreText } from '../ui/ScoreText';
import { formatMatchDateLabel } from '../../utils/dateLocale';
import { CheckCircle2, MinusCircle, XCircle } from 'lucide-react';
import { getTeamDisplayName } from '../../utils/teamDisplayName';

const tierStyles = {
  exact: {
    border: 'border-emerald-200',
    bg: 'bg-emerald-50/80',
    badge: 'bg-emerald-100 text-emerald-800',
    Icon: CheckCircle2
  },
  outcome: {
    border: 'border-amber-200',
    bg: 'bg-amber-50/80',
    badge: 'bg-amber-100 text-amber-900',
    Icon: MinusCircle
  },
  wrong: {
    border: 'border-red-200',
    bg: 'bg-red-50/80',
    badge: 'bg-red-100 text-red-800',
    Icon: XCircle
  },
  pending: {
    border: 'border-slate-200',
    bg: 'bg-slate-50/80',
    badge: 'bg-slate-100 text-slate-700',
    Icon: MinusCircle
  }
};

function TeamFace({ logoUrl, name }) {
  return (
    <div className="flex min-w-0 flex-col items-center justify-center gap-2 text-center sm:flex-row sm:gap-3">
      <img
        src={logoUrl || '/default-team.png'}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full border-2 border-white object-cover shadow-md ring-2 ring-slate-200/80 sm:h-12 sm:w-12"
      />
      <span className="line-clamp-2 max-w-full text-sm font-bold leading-snug text-slate-900 sm:text-base">
        {name || '—'}
      </span>
    </div>
  );
}

export function FinishedGameCard({ game, userBet }) {
  const { t, isRTL, language } = useLanguage();
  const home = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
  const away = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};
  const predicted = userBet?.predictedResult;
  const finalResult = game.finalResult;
  const points = userBet?.isCalculated ? userBet.points : null;
  const tier =
    points === null || points === undefined
      ? getPredictionTier(predicted, finalResult)
      : points === 3
        ? 'exact'
        : points === 1
          ? 'outcome'
          : points === 0
            ? 'wrong'
            : getPredictionTier(predicted, finalResult);

  const style = tierStyles[tier] || tierStyles.pending;
  const Icon = style.Icon;
  const dateLine = formatMatchDateLabel(game.date, language);

  const tierLabel =
    tier === 'exact'
      ? t('tierExact')
      : tier === 'outcome'
        ? t('tierOutcome')
        : tier === 'wrong'
          ? t('tierWrong')
          : t('tierPending');

  return (
    <Card className={`w-full border-2 ${style.border} ${style.bg}`}>
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="flex w-full flex-col gap-5"
      >
        <div className="flex w-full flex-wrap items-center gap-2">
          {game.round && (
            <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
              {game.round}
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${style.badge}`}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {tierLabel}
          </span>
        </div>

        <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-2xl bg-white/70 p-4 ring-1 ring-slate-200/60 sm:gap-4 sm:p-5">
          <TeamFace logoUrl={home.logoUrl} name={getTeamDisplayName(home, language)} />
          <span className="select-none px-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 sm:text-xs">
            {t('vs')}
          </span>
          <TeamFace logoUrl={away.logoUrl} name={getTeamDisplayName(away, language)} />
        </div>

        {dateLine ? (
          <time
            dateTime={game.date}
            className="text-xs font-medium text-slate-600 tabular-nums sm:text-sm"
          >
            {dateLine}
          </time>
        ) : null}

        <div className="grid w-full grid-cols-3 gap-2 rounded-2xl border border-slate-200/80 bg-white/90 p-3 sm:gap-4 sm:p-4">
          <div className="min-w-0 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
              {t('finishedCardFinal')}
            </p>
            <div className="mt-1.5 flex justify-center">
              <ScoreText score={finalResult} className="text-lg font-bold text-slate-900 sm:text-xl" />
            </div>
          </div>
          <div className="min-w-0 border-s border-slate-200/80 px-1 text-center sm:px-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
              {t('finishedCardYou')}
            </p>
            <div className="mt-1.5 flex justify-center">
              {predicted ? (
                <ScoreText score={predicted} className="text-lg font-bold text-slate-800 sm:text-xl" />
              ) : (
                <span className="text-lg font-bold text-slate-400">—</span>
              )}
            </div>
          </div>
          <div className="min-w-0 border-s border-slate-200/80 ps-1 text-center sm:ps-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
              {t('finishedCardPts')}
            </p>
            <p
              className={`mt-1.5 text-2xl font-bold tabular-nums sm:text-3xl ${
                points === 3
                  ? 'text-emerald-600'
                  : points === 1
                    ? 'text-amber-600'
                    : points === 0
                      ? 'text-red-600'
                      : 'text-slate-400'
              }`}
            >
              {points !== null && points !== undefined ? `+${points}` : '—'}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
